/**
 * ============================================================================
 * ACTIVITY WEBHOOK SERVICE - Phase 3
 * ============================================================================
 * Webhook system for activity-triggered integrations
 *
 * Features:
 * - Webhook registration and management
 * - Event-based webhook delivery
 * - Retry logic with exponential backoff
 * - HMAC signature verification
 * - Delivery logging and monitoring
 *
 * @pattern Phase 4 - IService + ServiceResponse + typedSupabase
 * @version 1.0.0
 * @date 2026-02-20
 */

import { typedSupabase } from "../lib/typed-supabase";
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError } from "./service-registry";
import { logger } from "../lib/logger";
// crypto imported dynamically to avoid pulling Node built-in into client bundle.
// The methods that use it (generateWebhookSecret, generateSignature) are
// server-only operations called from async service methods.

// ============================================================================
// TYPES
// ============================================================================

export interface ActivityWebhook {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  url: string;
  secret: string;
  events: string[];
  enabled: boolean;
  retry_config: {
    max_retries: number;
    backoff_multiplier: number;
    initial_delay_ms: number;
  };
  headers: Record<string, string>;
  created_at: string;
  updated_at: string;
  created_by: string;
  last_triggered_at: string | null;
  total_deliveries: number;
  successful_deliveries: number;
  failed_deliveries: number;
}

export interface WebhookDeliveryLog {
  id: string;
  webhook_id: string;
  activity_id: string;
  activity_domain: string;
  event_type: string;
  payload: Record<string, unknown>;
  request_headers: Record<string, string> | null;
  response_status: number | null;
  response_body: string | null;
  response_headers: Record<string, string> | null;
  delivery_attempt: number;
  success: boolean;
  error_message: string | null;
  delivered_at: string;
  duration_ms: number | null;
}

export interface CreateWebhookParams {
  organizationId: string;
  name: string;
  description?: string;
  url: string;
  events: string[];
  headers?: Record<string, string>;
  retryConfig?: {
    max_retries?: number;
    backoff_multiplier?: number;
    initial_delay_ms?: number;
  };
}

export interface UpdateWebhookParams {
  name?: string;
  description?: string;
  url?: string;
  events?: string[];
  enabled?: boolean;
  headers?: Record<string, string>;
  retryConfig?: {
    max_retries?: number;
    backoff_multiplier?: number;
    initial_delay_ms?: number;
  };
}

export interface DeliverWebhookParams {
  webhookId: string;
  activityId: string;
  activityDomain: string;
  eventType: string;
  payload: Record<string, unknown>;
}

export interface WebhookDeliveryResult {
  success: boolean;
  statusCode?: number;
  responseBody?: string;
  error?: string;
  duration: number;
}

// ============================================================================
// SERVICE INTERFACE
// ============================================================================

export interface IActivityWebhookService extends IService {
  /**
   * Create a new webhook registration
   */
  createWebhook(params: CreateWebhookParams): Promise<ServiceResponse<ActivityWebhook>>;

  /**
   * Get webhooks for an organization
   */
  getWebhooks(organizationId: string): Promise<ServiceResponse<ActivityWebhook[]>>;

  /**
   * Get a specific webhook by ID
   */
  getWebhook(webhookId: string): Promise<ServiceResponse<ActivityWebhook>>;

  /**
   * Update an existing webhook
   */
  updateWebhook(webhookId: string, updates: UpdateWebhookParams): Promise<ServiceResponse<ActivityWebhook>>;

  /**
   * Delete a webhook
   */
  deleteWebhook(webhookId: string): Promise<ServiceResponse<void>>;

  /**
   * Deliver webhook for an activity event
   */
  deliverWebhook(params: DeliverWebhookParams): Promise<ServiceResponse<WebhookDeliveryResult>>;

  /**
   * Get webhook delivery logs
   */
  getDeliveryLogs(webhookId: string, limit?: number): Promise<ServiceResponse<WebhookDeliveryLog[]>>;

  /**
   * Get webhooks for a specific event type
   */
  getWebhooksForEvent(organizationId: string, eventType: string): Promise<ServiceResponse<ActivityWebhook[]>>;

  /**
   * Test webhook delivery (send test payload)
   */
  testWebhook(webhookId: string): Promise<ServiceResponse<WebhookDeliveryResult>>;
}

// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================

export class ActivityWebhookService implements IActivityWebhookService {
  readonly name = "ActivityWebhookService";

  async initialize(): Promise<void> {
    logger.info("✅ ActivityWebhookService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("🧹 ActivityWebhookService disposed");
  }

  async createWebhook(params: CreateWebhookParams): Promise<ServiceResponse<ActivityWebhook>> {
    const { organizationId, name, description, url, events, headers = {}, retryConfig } = params;

    logger.debug("Creating webhook", { organizationId, name, url, events });

    try {
      // Generate webhook secret
      const secret = await this.generateWebhookSecret();

      const { data, error } = await typedSupabase
        .from("activity_webhooks" as any)
        .insert({
          organization_id: organizationId,
          name,
          description: description || null,
          url,
          secret,
          events,
          headers,
          retry_config: retryConfig || {
            max_retries: 3,
            backoff_multiplier: 2,
            initial_delay_ms: 1000,
          },
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message, code: "CREATE_FAILED", service: this.name, context: { params, error } };
      }

      return { success: true, data: data as ActivityWebhook };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "CREATE_FAILED", service: this.name, context: { params, error } };
    }
  }

  async getWebhooks(organizationId: string): Promise<ServiceResponse<ActivityWebhook[]>> {
    logger.debug("Fetching webhooks", { organizationId });

    try {
      const { data, error } = await typedSupabase
        .from("activity_webhooks" as any)
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (error) {
        return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { organizationId, error } };
      }

      return { success: true, data: (data as ActivityWebhook[]) || [] };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { organizationId, error } };
    }
  }

  async getWebhook(webhookId: string): Promise<ServiceResponse<ActivityWebhook>> {
    logger.debug("Fetching webhook", { webhookId });

    try {
      const { data, error } = await typedSupabase
        .from("activity_webhooks" as any)
        .select("*")
        .eq("id", webhookId)
        .single();

      if (error) {
        return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { webhookId, error } };
      }

      return { success: true, data: data as ActivityWebhook };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { webhookId, error } };
    }
  }

  async updateWebhook(
    webhookId: string,
    updates: UpdateWebhookParams
  ): Promise<ServiceResponse<ActivityWebhook>> {
    logger.debug("Updating webhook", { webhookId, updates });

    try {
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.url !== undefined) updateData.url = updates.url;
      if (updates.events !== undefined) updateData.events = updates.events;
      if (updates.enabled !== undefined) updateData.enabled = updates.enabled;
      if (updates.headers !== undefined) updateData.headers = updates.headers;
      if (updates.retryConfig !== undefined) updateData.retry_config = updates.retryConfig;

      const { data, error } = await typedSupabase
        .from("activity_webhooks" as any)
        .update(updateData)
        .eq("id", webhookId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message, code: "UPDATE_FAILED", service: this.name, context: { webhookId, updates, error } };
      }

      return { success: true, data: data as ActivityWebhook };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "UPDATE_FAILED", service: this.name, context: { webhookId, updates, error } };
    }
  }

  async deleteWebhook(webhookId: string): Promise<ServiceResponse<void>> {
    logger.debug("Deleting webhook", { webhookId });

    try {
      const { error } = await typedSupabase
        .from("activity_webhooks" as any)
        .delete()
        .eq("id", webhookId);

      if (error) {
        return { success: false, error: error.message, code: "DELETE_FAILED", service: this.name, context: { webhookId, error } };
      }

      return { success: true, data: undefined };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "DELETE_FAILED", service: this.name, context: { webhookId, error } };
    }
  }

  async deliverWebhook(params: DeliverWebhookParams): Promise<ServiceResponse<WebhookDeliveryResult>> {
    const { webhookId, activityId, activityDomain, eventType, payload } = params;

    logger.debug("Delivering webhook", { webhookId, activityId, eventType });

    try {
      // Get webhook details
      const webhookResponse = await this.getWebhook(webhookId);
      if (!webhookResponse.success || !webhookResponse.data) {
        return { success: false, error: "Webhook not found", code: "WEBHOOK_NOT_FOUND", service: this.name, context: { webhookId } };
      }

      const webhook = webhookResponse.data;

      if (!webhook.enabled) {
        return { success: false, error: "Webhook is disabled", code: "WEBHOOK_DISABLED", service: this.name, context: { webhookId } };
      }

      // Prepare payload with signature
      const timestamp = Date.now();
      const payloadWithMeta = {
        ...payload,
        webhook_id: webhookId,
        event_type: eventType,
        activity_domain: activityDomain,
        timestamp,
      };

      const signature = await this.generateSignature(webhook.secret, payloadWithMeta, timestamp);

      // Prepare headers
      const headers = {
        "Content-Type": "application/json",
        "X-Webhook-Signature": signature,
        "X-Webhook-Timestamp": timestamp.toString(),
        "X-Webhook-ID": webhookId,
        "User-Agent": "FlowState-Activity-Webhook/1.0",
        ...webhook.headers,
      };

      // Deliver with retry logic
      const result = await this.deliverWithRetry(
        webhook.url,
        payloadWithMeta,
        headers,
        webhook.retry_config
      );

      // Log delivery
      await this.logDelivery({
        webhookId,
        activityId,
        activityDomain,
        eventType,
        payload: payloadWithMeta,
        requestHeaders: headers,
        responseStatus: result.statusCode,
        responseBody: result.responseBody,
        responseHeaders: {},
        deliveryAttempt: 1,
        success: result.success,
        errorMessage: result.error,
        durationMs: result.duration,
      });

      return { success: true, data: result };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "DELIVERY_FAILED", service: this.name, context: { params, error } };
    }
  }

  async getDeliveryLogs(
    webhookId: string,
    limit: number = 50
  ): Promise<ServiceResponse<WebhookDeliveryLog[]>> {
    logger.debug("Fetching delivery logs", { webhookId, limit });

    try {
      const { data, error } = await typedSupabase
        .from("webhook_delivery_logs" as any)
        .select("*")
        .eq("webhook_id", webhookId)
        .order("delivered_at", { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { webhookId, error } };
      }

      return { success: true, data: (data as WebhookDeliveryLog[]) || [] };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { webhookId, error } };
    }
  }

  async getWebhooksForEvent(
    organizationId: string,
    eventType: string
  ): Promise<ServiceResponse<ActivityWebhook[]>> {
    logger.debug("Fetching webhooks for event", { organizationId, eventType });

    try {
      const { data, error } = await typedSupabase.rpc("get_webhooks_for_event", {
        p_organization_id: organizationId,
        p_event_type: eventType,
      });

      if (error) {
        return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { organizationId, eventType, error } };
      }

      return { success: true, data: (data as ActivityWebhook[]) || [] };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { organizationId, eventType, error } };
    }
  }

  async testWebhook(webhookId: string): Promise<ServiceResponse<WebhookDeliveryResult>> {
    logger.debug("Testing webhook", { webhookId });

    const testPayload = {
      event_type: "test",
      message: "This is a test webhook delivery",
      timestamp: new Date().toISOString(),
    };

    return this.deliverWebhook({
      webhookId,
      activityId: "test-activity-id",
      activityDomain: "test",
      eventType: "test",
      payload: testPayload,
    });
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  private async generateWebhookSecret(): Promise<string> {
    const { randomBytes } = await import("crypto");
    return randomBytes(32).toString("hex");
  }

  private async generateSignature(secret: string, payload: Record<string, unknown>, timestamp: number): Promise<string> {
    const { createHmac } = await import("crypto");
    const data = `${timestamp}.${JSON.stringify(payload)}`;
    return createHmac("sha256", secret).update(data).digest("hex");
  }
  private async deliverWithRetry(
    url: string,
    payload: Record<string, unknown>,
    headers: Record<string, string>,
    retryConfig: { max_retries: number; backoff_multiplier: number; initial_delay_ms: number }
  ): Promise<WebhookDeliveryResult> {
    const startTime = Date.now();
    let lastError: string | undefined;

    for (let attempt = 0; attempt <= retryConfig.max_retries; attempt++) {
      try {
        const response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        const responseBody = await response.text();
        const duration = Date.now() - startTime;

        if (response.ok) {
          return {
            success: true,
            statusCode: response.status,
            responseBody,
            duration,
          };
        }

        lastError = `HTTP ${response.status}: ${responseBody}`;

        // Don't retry on 4xx errors (except 429)
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          return {
            success: false,
            statusCode: response.status,
            responseBody,
            error: lastError,
            duration,
          };
        }
      } catch (error) {
        lastError = error instanceof Error ? error.message : "Unknown error";
      }

      // Wait before retry (exponential backoff)
      if (attempt < retryConfig.max_retries) {
        const delay = retryConfig.initial_delay_ms * Math.pow(retryConfig.backoff_multiplier, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    return {
      success: false,
      error: lastError || "All delivery attempts failed",
      duration: Date.now() - startTime,
    };
  }

  private async logDelivery(params: {
    webhookId: string;
    activityId: string;
    activityDomain: string;
    eventType: string;
    payload: Record<string, unknown>;
    requestHeaders: Record<string, string>;
    responseStatus?: number;
    responseBody?: string;
    responseHeaders: Record<string, string>;
    deliveryAttempt: number;
    success: boolean;
    errorMessage?: string;
    durationMs: number;
  }): Promise<void> {
    try {
      await typedSupabase.rpc("log_webhook_delivery", {
        p_webhook_id: params.webhookId,
        p_activity_id: params.activityId,
        p_activity_domain: params.activityDomain,
        p_event_type: params.eventType,
        p_payload: params.payload,
        p_request_headers: params.requestHeaders,
        p_response_status: params.responseStatus || null,
        p_response_body: params.responseBody || null,
        p_response_headers: params.responseHeaders,
        p_delivery_attempt: params.deliveryAttempt,
        p_success: params.success,
        p_error_message: params.errorMessage || null,
        p_duration_ms: params.durationMs,
      });
    } catch (error) {
      logger.error("Failed to log webhook delivery", { error });
    }
  }
}

// ============================================================================
// EXPORT SINGLETON
// ============================================================================

export const activityWebhookService = new ActivityWebhookService();
